import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { PWAState, PWAUpdateInfo } from '../types/pwa';
import { pwaService } from '../services/pwa.service';
import type { UserLanguage } from '@xtreed/academy-shared';
import i18n from '@/i18n';
import { isUserLanguage } from '@/i18n/languages';
import type { ThemePreference } from '@/lib/theme-mode';

interface PWAStoreState extends PWAState {
  install: () => Promise<boolean>;
  checkForUpdate: () => Promise<void>;
  skipWaiting: () => void;
  showInstallPrompt: () => void;
  dismissInstallPrompt: () => void;
  setOnlineStatus: (status: boolean) => void;
  setInstallPrompt: (prompt: BeforeInstallPromptEvent | null) => void;
  resetError: () => void;
  initialize: () => Promise<void>;
  updateInfo: PWAUpdateInfo | null;
  setUpdateInfo: (info: PWAUpdateInfo) => void;
  language: UserLanguage | null;
  tenantLanguage: UserLanguage | null;
  themePreference: ThemePreference | null;
  hasHydrated: boolean;
  setLanguage: (language: UserLanguage | null) => void;
  setThemePreference: (preference: ThemePreference | null) => void;
  setTenantLanguage: (language: UserLanguage | null) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

export const usePWAStore = create<PWAStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isInstalled: false,
        isInstallable: false,
        installPrompt: null,
        isOnline: navigator.onLine,
        hasUpdate: false,
        isLoading: false,
        error: null,
        lastUpdateCheck: null,
        installStats: {
          promptShown: 0,
          installed: 0,
          dismissed: 0,
        },
        updateInfo: null,
        language: null,
        tenantLanguage: null,
        themePreference: null,
        hasHydrated: false,

        setLanguage: (language) => {
          set({ language });
        },

        setThemePreference: (themePreference) => {
          set({ themePreference });
        },

        setTenantLanguage: (tenantLanguage) => {
          set({ tenantLanguage });
        },

        setHasHydrated: (hasHydrated) => {
          set({ hasHydrated });
        },

        // Actions
        initialize: async () => {
          set({ isLoading: true, error: null });

          try {
            const initialized = await pwaService.initialize();

            if (initialized) {
              // Set up update callback
              pwaService.onUpdateAvailable((info) => {
                get().setUpdateInfo(info);
                set({ hasUpdate: info.available });
              });

              // Check if already installed
              const isInstalled =
                window.matchMedia('(display-mode: standalone)').matches ||
                window.matchMedia('(display-mode: fullscreen)').matches ||
                (window.navigator as any).standalone === true;

              set({ isInstalled, isLoading: false });

              // Set up online/offline listeners
              const handleOnline = () => get().setOnlineStatus(true);
              const handleOffline = () => get().setOnlineStatus(false);

              window.addEventListener('online', handleOnline);
              window.addEventListener('offline', handleOffline);

              // Set up install prompt listener
              const handleBeforeInstallPrompt = (
                e: BeforeInstallPromptEvent,
              ) => {
                e.preventDefault();
                get().setInstallPrompt(e);
                set({ isInstallable: true });
              };

              window.addEventListener(
                'beforeinstallprompt',
                handleBeforeInstallPrompt,
              );

              // Clean up function would be handled by the component using this store
            } else {
              set({
                error: i18n.t('common:pwa.errors.initializationFailed'),
                isLoading: false,
              });
            }
          } catch (error) {
            console.error('PWA store initialization error:', error);
            set({
              error:
                error instanceof Error
                  ? error.message
                  : i18n.t('common:pwa.errors.unknown'),
              isLoading: false,
            });
          }
        },

        install: async () => {
          const { installPrompt } = get();

          if (!installPrompt) {
            set({
              error: i18n.t('common:pwa.errors.installPromptUnavailable'),
            });
            return false;
          }

          try {
            set({ isLoading: true, error: null });

            await installPrompt.prompt();
            const choiceResult = await installPrompt.userChoice;

            const newStats = { ...get().installStats };

            if (choiceResult.outcome === 'accepted') {
              set({
                isInstalled: true,
                isInstallable: false,
                installPrompt: null,
                isLoading: false,
                installStats: {
                  ...newStats,
                  installed: newStats.installed + 1,
                },
              });

              await pwaService.trackInstall();
              return true;
            } else {
              set({
                isLoading: false,
                installStats: {
                  ...newStats,
                  dismissed: newStats.dismissed + 1,
                },
              });
              return false;
            }
          } catch (error) {
            console.error('PWA install error:', error);
            set({
              error:
                error instanceof Error
                  ? error.message
                  : i18n.t('common:pwa.errors.installFailed'),
              isLoading: false,
            });
            return false;
          }
        },

        checkForUpdate: async () => {
          try {
            set({ isLoading: true, error: null });

            const updateInfo = await pwaService.checkForUpdates();

            set({
              hasUpdate: updateInfo.available,
              lastUpdateCheck: new Date(),
              isLoading: false,
              updateInfo,
            });
          } catch (error) {
            console.error('Update check error:', error);
            set({
              error:
                error instanceof Error
                  ? error.message
                  : i18n.t('common:pwa.errors.updateCheckFailed'),
              isLoading: false,
            });
          }
        },

        skipWaiting: () => {
          try {
            pwaService.skipWaiting();
            set({ hasUpdate: false, updateInfo: null });
          } catch (error) {
            console.error('Skip waiting error:', error);
            set({
              error:
                error instanceof Error
                  ? error.message
                  : i18n.t('common:pwa.errors.skipWaitingFailed'),
            });
          }
        },

        showInstallPrompt: () => {
          const { installPrompt, installStats } = get();

          if (installPrompt) {
            set({
              installStats: {
                ...installStats,
                promptShown: installStats.promptShown + 1,
              },
            });

            // The actual prompt will be triggered by calling install()
          } else {
            set({
              error: i18n.t('common:pwa.errors.installPromptUnavailable'),
            });
          }
        },

        dismissInstallPrompt: () => {
          const { installStats } = get();

          set({
            isInstallable: false,
            installPrompt: null,
            installStats: {
              ...installStats,
              dismissed: installStats.dismissed + 1,
            },
          });
        },

        setOnlineStatus: (status: boolean) => {
          set({ isOnline: status });
        },

        setInstallPrompt: (prompt: BeforeInstallPromptEvent | null) => {
          set({
            installPrompt: prompt,
            isInstallable: Boolean(prompt),
          });
        },

        setUpdateInfo: (info: PWAUpdateInfo) => {
          set({ updateInfo: info });
        },

        resetError: () => {
          set({ error: null });
        },
      }),
      {
        name: 'pwa-store',
        partialize: (state) => ({
          installStats: state.installStats,
          lastUpdateCheck: state.lastUpdateCheck,
          isInstalled: state.isInstalled,
          language: state.language,
          themePreference: state.themePreference,
        }),
        onRehydrateStorage: () => (state) => {
          if (!state) return;
          const persisted = state.language;
          if (persisted !== null && !isUserLanguage(persisted)) {
            state.setLanguage(null);
          }
          if (
            state.themePreference !== null &&
            state.themePreference !== 'light' &&
            state.themePreference !== 'dark'
          ) {
            state.setThemePreference(null);
          }
          state.setHasHydrated(true);
        },
      },
    ),
    { name: 'PWAStore' },
  ),
);

// Selectors for commonly used state
export const usePWAInstallable = () =>
  usePWAStore((state) => state.isInstallable);
export const usePWAInstalled = () => usePWAStore((state) => state.isInstalled);
export const usePWAOnline = () => usePWAStore((state) => state.isOnline);
export const usePWAUpdate = () => usePWAStore((state) => state.hasUpdate);
export const usePWALoading = () => usePWAStore((state) => state.isLoading);
export const usePWAError = () => usePWAStore((state) => state.error);
export const usePWALanguage = () => usePWAStore((state) => state.language);
